/** @jsx jsx */
/* eslint-disable camelcase */
import React from "react"
import { Styled, jsx } from "theme-ui"

// =======================
// Hjälpfunktioner datum/sort
// =======================
const isTBA = (date) => String(date).toLowerCase() === "tba"

// Parse "YYYY-MM-DD" som UTC (robust mot tidszoner)
const toTime = (dateStr) => {
  if (!dateStr || isTBA(dateStr)) return NaN
  const parts = String(dateStr).split("-").map(Number)
  if (parts.length !== 3) return NaN
  const [y, m, d] = parts
  if (!y || !m || !d) return NaN
  return Date.UTC(y, m - 1, d)
}

// "Idag" som UTC midnatt
const todayUTC = () => {
  const now = new Date()
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
}
// =======================
// Listan (sorterar & renderar)
// =======================
export const ShowList = ({ shows = [] }) => {
  const today = todayUTC()

  const tba = []
  const upcoming = []
  const past = []

  shows.forEach((s) => {
    const { date } = s

    // TBA eller tomt datum → först i listan, i originalordning
    if (!date || isTBA(date) || Number.isNaN(toTime(date))) {
      tba.push(s)
      return
    }

    const time = toTime(date)

    if (time >= today) {
      // Kommande gig
      upcoming.push(s)
    } else {
      // Redan passerade
      past.push(s)
    }
  })

  // Kommande: närmast först (stigande datum)
  upcoming.sort((a, b) => toTime(a.date) - toTime(b.date))

  // Utgångna: nyss passerade först (fallande datum)
  past.sort((a, b) => toTime(b.date) - toTime(a.date))

  const ordered = [...tba, ...upcoming, ...past]

  return (
    <ul sx={{ listStyle: "none", m: 0, p: 0 }}>
      {ordered.map((s) => (
        <ShowItem key={s.id} {...s} />
      ))}
    </ul>
  )
}

// =======================
// En enskild show (rad)
// =======================
const ShowItem = ({ id, name, date, location, info_url, map_url }) => {
  const expired = !isTBA(date) && toTime(date) < todayUTC()
  const dateObj = !isTBA(date) ? new Date(date) : null // jämför mot dagens datum (UTC)

  if (isTBA(date)) {
    return (
      <li
        key={id}
        className="GtmShowItem"
        sx={{
          variant: "components.show",
          opacity: expired ? 0.2 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <time
          className="GtmShowItem__date"
          sx={{ variant: "components.show.date" }}
        >
          TBA
          <span
            className="GtmShowItem__day"
            sx={{ variant: "components.show.date.day" }}
          />
        </time>
        <div sx={{ variant: "components.show.colWrapper" }}>
          <div sx={{ variant: "components.show.desc" }}>
            <Styled.h4
              className="GtmShowItem__title"
              sx={{ m: 0, pt: [0, 2], pb: [1, 2] }}
            >
              {name}
            </Styled.h4>
            <div sx={{ variant: "textStyles.itemSubheading" }}>
              {location}
              {map_url && (
                <>
                  {" "}
                  &middot;{" "}
                  <Styled.a href={map_url} rel="external nofollow">
                    <strong>Map</strong>
                  </Styled.a>
                </>
              )}
            </div>
          </div>
          <div sx={{ variant: "components.show.rsvpLink" }}>
            {info_url ? (
              <a
                href={info_url}
                rel="external nofollow"
                sx={{ variant: "textStyles.button" }}
              >
                Tickets
              </a>
            ) : (
              <small sx={{ color: "muted" }}>No RSVP info</small>
            )}
          </div>
        </div>
      </li>
    )
  }

  return (
    <li
      key={id}
      className="GtmShowItem"
      sx={{
        variant: "components.show",
        opacity: expired ? 0.2 : 1,
        transition: "opacity 0.3s ease",
      }}
    >
      <time
        dateTime={date}
        aria-label={
          dateObj
            ? dateObj.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : undefined
        }
        className="GtmShowItem__date"
        sx={{ variant: "components.show.date" }}
      >
        <span
          className="GtmShowItem__month"
          sx={{ variant: "components.show.date.month" }}
        >
          {dateObj.toLocaleDateString("en-GB", { month: "short" })}
        </span>
        <span
          className="GtmShowItem__day"
          sx={{ variant: "components.show.date.day" }}
        >
          {dateObj.toLocaleDateString("en-GB", { day: "numeric" })}
        </span>
        <span
          className="GtmShowItem__year"
          sx={{ variant: "components.show.date.year" }} // <- NY
        >
          {dateObj.toLocaleDateString("en-GB", { year: "numeric" })}
        </span>
      </time>
      <div sx={{ variant: "components.show.colWrapper" }}>
        <div sx={{ variant: "components.show.desc" }}>
          <Styled.h4
            className="GtmShowItem__title"
            sx={{ m: 0, pt: [0, 2], pb: [1, 2] }}
          >
            {name}
          </Styled.h4>
          <div sx={{ variant: "textStyles.itemSubheading" }}>
            {location}
            {map_url && (
              <>
                {" "}
                &middot;{" "}
                <Styled.a href={map_url} rel="external nofollow">
                  <strong>Map</strong>
                </Styled.a>
              </>
            )}
          </div>
        </div>
        <div sx={{ variant: "components.show.rsvpLink" }}>
          {info_url ? (
            <a
              href={info_url}
              rel="external nofollow"
              sx={{ variant: "textStyles.button" }}
            >
              Tickets
            </a>
          ) : (
            <small sx={{ color: "muted" }}>No RSVP info</small>
          )}
        </div>
      </div>
    </li>
  )
}

export default ShowItem
